// The calendar-link controller (bead hf-7o6).
//
// One state machine over the three things a member can do to their own feed: make a
// link, replace it, turn it off. It knows nothing about Convex — it is handed the
// functions that perform each one, exactly as `useClaim` is.
//
// ── THE TOKEN LIVES HERE, AND ONLY HERE, AND ONLY FOR A MOMENT ──────────────────
//
// The backend returns the link ONCE and stores only its hash, so this controller's
// state is the only place the raw link ever exists. Three rules follow, and every one
// of them is a test:
//
//   1. REPLACING CLEARS THE OLD ONE FIRST. The previous link is dead the instant the
//      new mint returns, so a screen still showing it is showing a member a link that
//      no longer works — the one mistake that would make rotation worse than useless.
//   2. TURNING OFF CLEARS IT TOO. Nothing that has been revoked stays on screen.
//   3. A FAILURE CLEARS IT. If we do not know what happened, we do not keep showing a
//      credential we may no longer be able to explain.
//
// ── WHY EVERY MINT CARRIES A FRESH IDEMPOTENCY KEY ──────────────────────────────
//
// `mintFeedToken` declares an idempotency scope, so a retried key replays and returns
// NO token (convex/boundary/wrappers.ts). That sounds like the wrong thing to want
// here — but the alternative is worse. Without a key, a transport-level retry mints
// twice and the client may resolve with the FIRST link while the second has already
// overwritten it: the member carefully saves a link that is already dead, and gets no
// hint of it.
//
// With a key, a retry is recognised and answered honestly: "a link was made and we
// can't show it — replace it to get one you can see." A live link the member has
// never seen is not left lying around, because replacing kills it. That is a worse
// message and a better outcome, so a fresh key per press it is.

import type { CommonRejection } from "@herefor/contracts";
import { useCallback, useRef, useState } from "react";

import type {
  MintFeedTokenResult,
  RevokeFeedTokenResult,
  SetFeedTitleModeResult,
} from "../../convex/calendarFeed";

/** How much a member's own calendar is allowed to say (CAL-003). */
export type FeedTitleMode = "discreet" | "detailed";

/** Which press is in flight, so the right control can show it. */
export type FeedAction = "create" | "replace" | "turnOff" | "titleMode";

export type FeedActionState =
  | { readonly status: "idle" }
  | { readonly status: "working"; readonly action: FeedAction }
  /**
   * The one moment the link exists on screen. `replaced` is true when this link
   * displaced an earlier one, because "here is your link" and "your old link has
   * stopped working — here is the new one" are different things to be told.
   */
  | { readonly status: "revealed"; readonly link: string; readonly replaced: boolean }
  /** A retry the server recognised. A link exists; we cannot show it. */
  | { readonly status: "alreadyCreated" }
  | { readonly status: "turnedOff" }
  | { readonly status: "refused"; readonly rejection: CommonRejection };

export interface UseCalendarFeedOptions {
  /** Mints or rotates. Takes the key so the caller does not have to invent one. */
  readonly mint: (idempotencyKey: string) => Promise<MintFeedTokenResult>;
  readonly revoke: () => Promise<RevokeFeedTokenResult>;
  readonly setTitleMode: (mode: FeedTitleMode) => Promise<SetFeedTitleModeResult>;
  /** Builds the link a member copies, from the token the mint returned. */
  readonly linkFor: (token: string) => string;
  /** Overridable so a test can assert the key is fresh per press. */
  readonly newIdempotencyKey?: () => string;
}

export interface CalendarFeedController {
  readonly state: FeedActionState;
  /** First link for a member who has none. */
  readonly create: () => Promise<void>;
  /** Rotation. The same backend call as `create`; a different thing to be told. */
  readonly replace: () => Promise<void>;
  readonly turnOff: () => Promise<void>;
  readonly chooseTitleMode: (mode: FeedTitleMode) => Promise<void>;
  /** Put the panel away. Dismisses a message; undoes nothing. */
  readonly dismiss: () => void;
}

/** A rejection that stands in for "we do not know", so nothing is shown optimistically. */
const TRANSPORT_FAILURE: CommonRejection = { kind: "validation_failed", issues: [] };

export function useCalendarFeed({
  mint,
  revoke,
  setTitleMode,
  linkFor,
  newIdempotencyKey = () => crypto.randomUUID(),
}: UseCalendarFeedOptions): CalendarFeedController {
  const [state, setState] = useState<FeedActionState>({ status: "idle" });

  /**
   * In-flight guard in a ref, not in state.
   *
   * Same reason as `useClaim`: a disabled button is not enough, because a second press
   * can be dispatched before React re-renders and state read in that window is stale.
   * Here it matters more than there — two mints in flight means two rotations, and the
   * member is shown whichever resolves last while having possibly saved the other.
   */
  const inFlight = useRef(false);

  const run = useCallback(
    async (action: FeedAction, perform: () => Promise<FeedActionState>): Promise<void> => {
      if (inFlight.current) return;
      inFlight.current = true;
      // Clearing to `working` is what drops any revealed link off the screen before
      // the call that will invalidate it has even returned. Deliberate: for the
      // moment in between we would rather show nothing than show a dying link.
      setState({ status: "working", action });

      let next: FeedActionState;
      try {
        next = await perform();
      } catch {
        next = { status: "refused", rejection: TRANSPORT_FAILURE };
      }

      inFlight.current = false;
      setState(next);
    },
    [],
  );

  const mintLink = useCallback(
    (action: Extract<FeedAction, "create" | "replace">) =>
      run(action, async () => {
        const outcome = await mint(newIdempotencyKey());

        // A recognised retry. The handler did not run again and there is no token in
        // the answer — see the note at the top of this file.
        if (outcome.status === "replayed") return { status: "alreadyCreated" };

        if (!outcome.value.ok) {
          return { status: "refused", rejection: outcome.value.rejection };
        }

        return {
          status: "revealed",
          link: linkFor(outcome.value.value.token),
          replaced: action === "replace",
        };
      }),
    [mint, newIdempotencyKey, linkFor, run],
  );

  const create = useCallback(() => mintLink("create"), [mintLink]);
  const replace = useCallback(() => mintLink("replace"), [mintLink]);

  const turnOff = useCallback(
    () =>
      run("turnOff", async () => {
        const outcome = await revoke();
        if (outcome.status === "replayed") return { status: "turnedOff" };
        if (!outcome.value.ok) {
          return { status: "refused", rejection: outcome.value.rejection };
        }
        // `revoked: false` means there was nothing active to turn off. The member's
        // link is off either way, which is the only thing they asked about.
        return { status: "turnedOff" };
      }),
    [revoke, run],
  );

  const chooseTitleMode = useCallback(
    (mode: FeedTitleMode) =>
      run("titleMode", async () => {
        const outcome = await setTitleMode(mode);
        if (outcome.status === "replayed") return { status: "idle" };
        if (!outcome.value.ok) {
          return { status: "refused", rejection: outcome.value.rejection };
        }
        // Back to idle rather than to a confirmation: the choice is visible in the
        // control the member just used, and a panel congratulating them on it would
        // be noise.
        return { status: "idle" };
      }),
    [setTitleMode, run],
  );

  const dismiss = useCallback(() => {
    inFlight.current = false;
    setState({ status: "idle" });
  }, []);

  return { state, create, replace, turnOff, chooseTitleMode, dismiss };
}
